import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface WalmartConfigProps {
  config: {
    stockLevel: number;
    batchSize: number;
    requestsPerSecond: number;
    handlingTimeOmd: number;
    providerSpecificHandlingTime: number;
    updateFlagValue: number;
    priceMarkupPercentage?: number; // Phase 2.1 - Markup % para cálculo de preço
    enableMinimumPriceCheck?: boolean;
    minimumPriceThreshold?: number;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface QuotaData {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  lastRequestAt: string | null;
}

/**
 * Componente de configuração específico para o fornecedor Walmart
 */
const WalmartConfig: React.FC<WalmartConfigProps> = ({ config, onChange }) => {
  const [quota, setQuota] = useState<QuotaData | null>(null);
  const [loadingQuota, setLoadingQuota] = useState(true);
  
  // Buscar quota ao montar o componente
  useEffect(() => {
    fetchQuota();
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchQuota, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchQuota = async () => {
    try {
      const response = await axios.get('/api/stores/walmart/quota');
      if (response.data.success) {
        setQuota(response.data.quota);
      }
    } catch (error) {
      console.error('Error fetching quota:', error);
    } finally {
      setLoadingQuota(false);
    }
  };
  
  const getQuotaColor = (percentage: number) => {
    if (percentage < 50) return 'bg-green-500';
    if (percentage < 75) return 'bg-yellow-500';
    if (percentage < 90) return 'bg-orange-500';
    return 'bg-red-500';
  };
  
  const getQuotaTextColor = (percentage: number) => {
    if (percentage < 50) return 'text-green-700';
    if (percentage < 75) return 'text-yellow-700';
    if (percentage < 90) return 'text-orange-700';
    return 'text-red-700';
  };
  
  return (
    <div className="space-y-4">
      {/* Quota Display */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-blue-900 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Daily API Quota
          </h3>
          <button
            onClick={fetchQuota}
            className="text-blue-600 hover:text-blue-800 text-sm"
            title="Refresh quota"
          >
            ↻ Refresh
          </button>
        </div>
        
        {loadingQuota ? (
          <div className="text-sm text-gray-600">Loading quota...</div>
        ) : quota ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-2xl font-bold ${getQuotaTextColor(quota.percentage)}`}>
                {quota.used.toLocaleString()} / {quota.limit.toLocaleString()}
              </span>
              <span className={`text-sm font-semibold ${getQuotaTextColor(quota.percentage)}`}>
                {quota.percentage.toFixed(1)}% used
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${getQuotaColor(quota.percentage)}`}
                style={{ width: `${Math.min(quota.percentage, 100)}%` }}
              />
            </div>
            
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>
                <strong>{quota.remaining.toLocaleString()}</strong> requests remaining
              </span>
              {quota.lastRequestAt && (
                <span>
                  Last: {new Date(quota.lastRequestAt).toLocaleTimeString()}
                </span>
              )}
            </div>
            
            {quota.percentage > 90 && (
              <div className="mt-2 text-xs text-red-600 font-semibold">
                ⚠️ Warning: Approaching daily limit!
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-600">No quota data available</div>
        )}
      </div>
      
      {/* Existing Config Fields */}
      <div>
        <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700 mb-1">
          Nível de Estoque (Walmart)
        </label>
        <input
          id="stockLevel"
          type="number"
          name="stockLevel"
          value={config.stockLevel || ''}
          onChange={onChange}
          className="input"
          aria-label="Nível de estoque para Walmart"
        />
        <p className="text-sm text-gray-500 mt-1">
          Limite máximo de estoque a ser considerado para produtos do Walmart.
        </p>
      </div>
      
      <div>
        <label htmlFor="handlingTimeOmd" className="block text-sm font-medium text-gray-700 mb-1">
          Tempo de Manuseio OMD (dias)
        </label>
        <input
          id="handlingTimeOmd"
          type="number"
          name="handlingTimeOmd"
          value={config.handlingTimeOmd || ''}
          onChange={onChange}
          className="input"
          aria-label="Tempo de manuseio da OMD em dias"
        />
        <p className="text-sm text-gray-500 mt-1">
          Tempo de manuseio da OMD para produtos do Walmart.
        </p>
      </div>
      
      <div>
        <label htmlFor="walmartHandlingTime" className="block text-sm font-medium text-gray-700 mb-1">
          Tempo de Manuseio Walmart (dias)
        </label>
        <input
          id="walmartHandlingTime"
          type="number"
          name="providerSpecificHandlingTime"
          value={config.providerSpecificHandlingTime || ''}
          onChange={onChange}
          className="input"
          aria-label="Tempo de manuseio do Walmart em dias"
        />
        <p className="text-sm text-gray-500 mt-1">
          Tempo de manuseio específico do Walmart para seus produtos.
        </p>
      </div>
      
      <div>
        <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
          Tamanho do Lote
        </label>
        <input
          id="batchSize"
          type="number"
          name="batchSize"
          value={config.batchSize || ''}
          onChange={onChange}
          className="input"
          aria-label="Tamanho do lote"
        />
        <p className="text-sm text-gray-500 mt-1">
          Número de produtos a serem processados em cada lote durante o envio para a Amazon.
        </p>
      </div>
      
      <div>
        <label htmlFor="requestsPerSecond" className="block text-sm font-medium text-gray-700 mb-1">
          Requisições por Segundo
        </label>
        <input
          id="requestsPerSecond"
          type="number"
          name="requestsPerSecond"
          value={config.requestsPerSecond || ''}
          onChange={onChange}
          className="input"
          aria-label="Requisições por segundo"
        />
        <p className="text-sm text-gray-500 mt-1">
          Taxa de requisições à API do Walmart (limite: 200 req/min = 3.33 req/s).
        </p>
      </div>
      
      <div>
        <label htmlFor="updateFlagValue" className="block text-sm font-medium text-gray-700 mb-1">
          Código de Atualização Walmart
        </label>
        <input
          id="updateFlagValue"
          type="number"
          name="updateFlagValue"
          value={config.updateFlagValue || ''}
          onChange={onChange}
          className="input"
          aria-label="Código de atualização Walmart"
        />
        <p className="text-sm text-gray-500 mt-1">
          Identificador usado para marcar produtos atualizados pelo Walmart no banco de dados. 
          Recomendado: 2
        </p>
      </div>

      <div>
        <label htmlFor="minimumPriceThreshold" className="block text-sm font-medium text-gray-700 mb-1">
          Preço Mínimo ($)
        </label>
        <input
          id="minimumPriceThreshold"
          type="number"
          name="minimumPriceThreshold"
          value={config.minimumPriceThreshold || ''}
          onChange={onChange}
          className="input"
          aria-label="Preço mínimo"
          min="0"
          step="0.01"
        />
        <p className="text-xs text-gray-500 mt-1">
          Produtos com preço abaixo deste valor serão marcados como "Out of Stock".
        </p>
      </div>
      
      <div>
        <label htmlFor="enableMinimumPriceCheck" className="flex items-center space-x-2">
          <input
            id="enableMinimumPriceCheck"
            type="checkbox"
            name="enableMinimumPriceCheck"
            checked={config.enableMinimumPriceCheck || false}
            onChange={onChange}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Ativar verificação de preço mínimo</span>
        </label>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-4">
        <h3 className="text-blue-800 font-medium mb-2">Informações do Walmart</h3>
        <p className="text-sm text-blue-700">
          O Walmart fornece informações simplificadas sobre <strong>preços e disponibilidade</strong> de produtos.
          A API atualiza apenas 2 campos: <code className="bg-blue-100 px-1 rounded">supplier_price</code> e <code className="bg-blue-100 px-1 rounded">supplier_availability</code>.
          O sistema utilizará o tempo de manuseio configurado acima para todos os produtos.
        </p>
        <p className="text-sm text-blue-700 mt-2">
          <strong>⚠️ Limite de API:</strong> 200 requisições por minuto (3.33 req/s)
        </p>
      </div>

      {/* Seção: Phase 2.1 - Price Markup */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">
          💰 Phase 2.1 - Price Markup
        </h3>
        
        <div>
          <label htmlFor="priceMarkupPercentage" className="block text-sm font-medium text-gray-700 mb-1">
            Markup Percentage (%)
          </label>
          <input
            id="priceMarkupPercentage"
            type="number"
            name="priceMarkupPercentage"
            min="0"
            step="0.1"
            value={config.priceMarkupPercentage || 15}
            onChange={onChange}
            className="input"
          />
          <p className="text-sm text-gray-500 mt-1">
            Percentual de markup aplicado ao calcular o preço Amazon.
          </p>
          <p className="text-sm text-purple-700 mt-2">
            <strong>ℹ️ Fórmula:</strong> Amazon Price = (supplier_price + shipping) × (1 + markup% / 100)
          </p>
          <p className="text-sm text-purple-700 mt-1">
            <strong>📝 Exemplo:</strong> Produto $10 + Shipping $5 com 15% markup = $17.25
          </p>
        </div>
      </div>
    </div>
  );
};

export default WalmartConfig;
